import { Box, IconButton, useTheme, styled } from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { PdfPreviewBox as PdfPreviewBoxComponent } from './PdfPreviewBox';

const PreviewImage = styled('img')(({ theme }) => ({
  width: 100,
  height: 100,
  objectFit: 'cover',
  borderRadius: 8,
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    opacity: 0.8,
  },
}));

const FullscreenImage = styled('img')({
  maxWidth: '90%',
  maxHeight: '90%',
  objectFit: 'contain',
});

interface ImagePreviewProps {
  imageUrl: string;
  onRemove: () => void;
  onImageClick?: () => void;
  isPdf?: boolean;
}

export const ImagePreview = ({
  imageUrl,
  onRemove,
  onImageClick,
  isPdf = false,
}: ImagePreviewProps) => {
  const theme = useTheme();
  const [showFullscreen, setShowFullscreen] = useState(false);

  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick();
    } else if (!isPdf) {
      setShowFullscreen(true);
    }
  };

  const handleCloseFullscreen = () => {
    setShowFullscreen(false);
  };

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove();
  };

  const handleImageStopPropagation = (
    e: React.MouseEvent<HTMLImageElement>
  ) => {
    e.stopPropagation();
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          marginRight: 0,
          marginBottom: 0,
        }}
      >
        {isPdf ? (
          <PdfPreviewBoxComponent onClick={handleImageClick} />
        ) : (
          <PreviewImage
            src={imageUrl}
            alt="Preview"
            onClick={handleImageClick}
          />
        )}
        <IconButton
          onClick={handleRemoveClick}
          size="small"
          sx={{
            position: 'absolute',
            top: 3,
            right: 3,
            backgroundColor: theme.palette.background.paper,
            width: 20,
            height: 20,
            boxShadow: theme.shadows[2],
            '&:hover': {
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
            },
          }}
          aria-label={isPdf ? 'Remove PDF' : 'Remove image'}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {showFullscreen &&
        typeof document !== 'undefined' &&
        createPortal(
          <Box
            onClick={handleCloseFullscreen}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              cursor: 'pointer',
            }}
          >
            <FullscreenImage
              src={imageUrl}
              alt="Full size preview"
              onClick={handleImageStopPropagation}
            />
            <IconButton
              onClick={handleCloseFullscreen}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
              }}
              aria-label="Close fullscreen"
            >
              <Close />
            </IconButton>
          </Box>,
          document.body
        )}
    </>
  );
};
